//#region [IMPORT]

// === THIRD-PARTY ===
import React, { createContext, useContext, useEffect, useState } from 'react';

// === STYLE / MATERIAL UI ===
import { useTheme } from '@mui/material/styles';
import {
  Box,
  Paper,
  TableContainer,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell
} from '@mui/material';

// === HOOKS ===
import { useSession } from 'pages/PageManager/components/SessionContext';

// === API ===
import { prokeyApi } from 'api/prokey';
import {
  subscribeAuditQueueInfo, unsubscribeAuditQueueInfo,
  on, removeListener,
} from 'api/prokey/prokey.websocket';

// === UTILS ===
import { getHexAlpha } from 'components/styles.utils';

//#endregion

//-----------------------------------------------------------------
export function AuditTaskList() {
  //-----------------------------------------------------------------

  //#region [VALUES] === STORE

  const { user_details } = useSession();

  const [audit_tasks, setAuditTasks] = useState({});

  const has_tasks = Object.keys(audit_tasks).length > 0;

  const is_auditor = user_details.Roles?.some((item)=>item.RoleName === 'auditor');

  //#endregion

  //#region [VALUES] === STYLE

  const theme = useTheme();
  const _styles = {
    audit_header: {
      variant: 'h5',
      color: theme.palette.primary.main,
      sx: {
        margin: '1rem',
      }
    },
    table_header_cell: {
      backgroundColor: theme.palette.primary.main + getHexAlpha(90),
      color: theme.palette.primary.contrastText,
    },
    container: {
      display : is_auditor ? '' : 'none',
      height   : 'calc(100% - 5rem)' ,
      width    : '100%'              , 
      padding  : '1rem'              ,
      overflow : 'auto'              ,
    }
  }

  //#endregion

  useEffect(() => {
    //-----------------------------------------------------------------
    function handleAuditQueueInfo(data) { setAuditTasks(data); };
      //-----------------------------------------------------------------

    on( 'AUDIT-QUEUE-INFO', handleAuditQueueInfo );

    subscribeAuditQueueInfo();

    return () => {
      unsubscribeAuditQueueInfo();

      removeListener( 'AUDIT-QUEUE-INFO', handleAuditQueueInfo);
    };
  }, []);

  //#region [green] Events

  //-----------------------------------------------------------------
  function onDoubleClick(event) {
    //-----------------------------------------------------------------
    prokeyApi.requestTask(true, event.currentTarget.id);
  }
  //#endregion

  //#region [DISPLAY]

  //-----------------------------------------------------------------
  const display = () => {
    //-----------------------------------------------------------------
    return (
      <Box sx={_styles.container}>
        <TableContainer component={Paper}>
          <Typography {..._styles.audit_header}> Audit Queue </Typography>
          <Table size='small' stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={_styles.table_header_cell} > { 'Loan Number'  } </TableCell>
                <TableCell sx={_styles.table_header_cell} > { 'Package Name' } </TableCell>
                <TableCell sx={_styles.table_header_cell} > { 'File Name'    } </TableCell>
                <TableCell sx={_styles.table_header_cell} > { 'Prokey User'  } </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>{ has_tasks ? displayData() : displayEmpty() }</TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  //-----------------------------------------------------------------
  function displayEmpty() {
    //-----------------------------------------------------------------
    return (
      <TableRow>
        <TableCell colSpan='7' className='allEmptyHere'>
          All empty here...
        </TableCell>
      </TableRow>
    );
  }

  //-----------------------------------------------------------------
  const displayData = () => {
    //-----------------------------------------------------------------
    const result = Object.values(audit_tasks).map( task => {
      const row_props = {
        key: task.id,
        id: task.id,
        onDoubleClick,
      }

      result.push(
        <TableRow {...row_props}>
          <TableCell key={0}>{ task.loan_number  }</TableCell>
          <TableCell key={1}>{ task.package_name }</TableCell>
          <TableCell key={2}>{ task.file_name    }</TableCell>
          <TableCell key={2}>{ task.user         }</TableCell>
        </TableRow>
      );
    });

    return result;
  };

  //#endregion

  return display();
}
